import { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import { Button, Chip } from '@c2fo/liquidity';
import { CheckIcon, GemLightIcon } from '@c2fo/liquidity/icons';
import PageBottomPortal from '@/enterprise/components/PageBottomPortal/PageBottomPortal';
import useGroupByBuyer from '@/enterprise/data/useGroupByBuyer';
import usePricingSuggestion from '@/enterprise/data/usePricingSuggestion';
import { useReporting } from '@/reporting';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import getTakerMarketDivisionTitle from '@/utils/getTakerMarketDivisionTitle';
import getTakerMarketName from '@/utils/getTakerMarketName';
import useLocaleFormat from '@/utils/useLocaleFormat';
import Skeleton from '../../Skeleton';
import NavigationBar from '../NavigationBar';
import { useOfferBuilderContext } from '../OfferBuilderContext';
import OfferSummary from '../OfferSummary';
import usePreferredRateEligibility from '../usePreferredRateEligibility';
import { buildTakerMarketKey, calculateDiscount } from '../utils';

const PreferredRateStepSkeleton = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <div className="lg:w-7/12">
        <div className="space-y-4 rounded-xl bg-deepBlue-50 p-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
      <div className="flex-1">
        <div className="lg:sticky lg:top-24">
          <div className="mb-2 text-lg font-medium">{t('builder.offerSummary')}</div>
          <div className="rounded-lg bg-deepBlue-50 p-2">
            <Skeleton className="h-60 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
};

const PreferredRateStepContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { asCurrency, asNumber } = useLocaleFormat();
  const { dispatch, state } = useOfferBuilderContext();
  const {
    selected,
    disableNavigation,
    submissionComplete,
    preferredRateSelected,
    workingTakerMarkets,
    preUpgradeRates,
  } = state;
  const { trackEnterpriseEvent } = useReporting();
  const { data: pricingSuggestion } = usePricingSuggestion({
    takerMarkets: selected,
    includePreferred: true,
  });

  const { showPreferredStep } = usePreferredRateEligibility(state, pricingSuggestion);

  useEffectOnce(() => {
    trackEnterpriseEvent('preferred-rate-upgrade::shown');
  });

  const buyers = useGroupByBuyer({ takerMarkets: selected, sortByEligible: true });

  // Build a lookup map for O(1) supplier market access
  const supplierMarketMap = useMemo(() => {
    const map = new Map<string, (typeof pricingSuggestion)['supplierMarkets'][number]>();

    pricingSuggestion?.supplierMarkets.forEach((sm) => {
      map.set(`${sm.marketUuid}-${sm.supplierDivisionUuid}`, sm);
    });

    return map;
  }, [pricingSuggestion]);

  // Calculate preferred rate and discount
  const preferredRateData = useMemo(() => {
    if (!pricingSuggestion) {
      return { preferredRate: 0, selectedRate: 0, preferredDiscount: 0 };
    }

    const items = selected.flatMap((tm) => {
      const supplierMarket = supplierMarketMap.get(`${tm.marketUuid}-${tm.takerDivisionUuid}`);
      const key = buildTakerMarketKey(tm);
      // After upgrade, workingTakerMarkets holds the preferred rates, so use the stored
      // pre-upgrade rate to keep the strikethrough displaying the original non-preferred rate.
      const preUpgradeRate = preUpgradeRates?.find((r) => r.takerMarketKey === key)?.rateInfo.rate;
      const workingRate = preferredRateSelected ? preUpgradeRate : workingTakerMarkets[key]?.rate;
      const preferredRate = supplierMarket?.preferred?.oneTime?.rate;

      if (!preferredRate || !workingRate) {
        return [];
      }

      return [
        {
          preferredRate,
          workingRate,
          dpeWeightedAvg: tm.eligibleDpeWeightedAvg,
          dpeWeightedSum: tm.eligibleDpeWeightedSum,
          invoiceAmount: tm.eligibleInvoiceAmountUsd,
        },
      ];
    });

    if (items.length === 0) {
      return { preferredRate: 0, selectedRate: 0, preferredDiscount: 0 };
    }

    const totalAmount = items.reduce((sum, item) => sum + item.invoiceAmount, 0);

    // Weighted average by invoice amount — using a simple weighted mean rather than
    // calculateAverageApr, which requires dpeWeightedSum and round-trips through
    // discount math, producing incorrect results when dpeWeightedSum >> dpeWeightedAvg.
    const preferredRate = items.reduce((sum, item) => sum + item.preferredRate * item.invoiceAmount, 0) / totalAmount;
    const selectedRate = items.reduce((sum, item) => sum + item.workingRate * item.invoiceAmount, 0) / totalAmount;

    const preferredDiscount = items.reduce((sum, item) => {
      return (
        sum +
        calculateDiscount({
          rate: item.preferredRate,
          invoiceAmount: item.invoiceAmount,
          dpeWeightedAvg: item.dpeWeightedAvg,
          isDiscount: false,
        })
      );
    }, 0);

    return { preferredRate, selectedRate, preferredDiscount };
  }, [pricingSuggestion, supplierMarketMap, selected, workingTakerMarkets, preferredRateSelected, preUpgradeRates]);

  // Guards
  if (selected.length === 0 || submissionComplete) {
    return <Navigate to={asEnterpriseLink('build-offer')} replace />;
  }

  // Allow staying on this step when the user has already accepted the upgrade —
  // at that point workingTakerMarkets rates equal the preferred rates so
  // showPreferredStep would become false and incorrectly redirect to review.
  if (!showPreferredStep && !preferredRateSelected) {
    return <Navigate to={asEnterpriseLink('build-offer/review')} replace />;
  }

  const onBackClick = () => {
    navigate(asEnterpriseLink('build-offer/rate'));
  };

  const onNextClick = () => {
    navigate(asEnterpriseLink('build-offer/review'));
  };

  const handleUpgrade = () => {
    trackEnterpriseEvent('preferred-rate-upgrade::selected');

    // Capture current working rates before overwriting with preferred rates
    const currentRates = selected
      .map((tm) => {
        const key = buildTakerMarketKey(tm);
        const workingMarket = workingTakerMarkets[key];
        if (workingMarket?.rate && workingMarket?.rateType) {
          return {
            takerMarketKey: key,
            rateInfo: {
              rate: workingMarket.rate,
              rateType: workingMarket.rateType,
              pricingStrategy: workingMarket.pricingStrategy,
              pricingRateUuid: workingMarket.pricingRateUuid,
              suggestedRate: workingMarket.suggestedRate,
              suggestRateType: workingMarket.suggestRateType,
              customRate: workingMarket.customRate,
              customRateType: workingMarket.customRateType,
            },
          };
        }

        return null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    dispatch({ type: 'SET_PRE_UPGRADE_RATES', payload: currentRates });

    // Build the preferred rates map and update working taker markets
    const preferredRatesMap: Record<
      string,
      { rate: number | undefined; projectedClearingPercentage: number | undefined } | undefined
    > = {};
    const rateUpdates: {
      takerMarketKey: string;
      rateInfo: { rate: number; rateType: 'APR'; pricingStrategy: 'nameYourRate' };
    }[] = [];

    selected.forEach((tm) => {
      const key = buildTakerMarketKey(tm);
      const supplierMarket = supplierMarketMap.get(`${tm.marketUuid}-${tm.takerDivisionUuid}`);
      const preferredRate = supplierMarket?.preferred?.oneTime?.rate;
      const projectedClearingPercentage = supplierMarket?.apr?.oneTime?.projectedClearingPercentage;

      preferredRatesMap[key] = { rate: preferredRate, projectedClearingPercentage };

      if (preferredRate !== undefined) {
        rateUpdates.push({
          takerMarketKey: key,
          rateInfo: {
            rate: preferredRate,
            rateType: 'APR',
            pricingStrategy: 'nameYourRate',
          },
        });
      }
    });

    dispatch({ type: 'UPDATE_WORKING_TAKER_MARKETS_RATE', payload: rateUpdates });
    dispatch({ type: 'SET_PREFERRED_RATE_SELECTED', payload: true });
    dispatch({ type: 'SET_PREFERRED_RATES_BY_MARKET', payload: preferredRatesMap });
  };

  const handleUndoUpgrade = () => {
    trackEnterpriseEvent('preferred-rate-upgrade::deselected', {
      source: 'preferred-rate-step',
    });
    dispatch({ type: 'CLEAR_PREFERRED_RATE_STATE' });
  };

  return (
    <>
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="lg:w-7/12">
          <div className="mb-2 text-lg font-medium">{t('builder.preferredOfferOption')}</div>
          {/* Main Preferred Card */}
          <div className="rounded-lg bg-purple-100 p-4">
            {/* Header Row */}
            <div className="mb-4 flex items-center justify-between gap-6">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
                <div className="flex flex-row items-center sm:flex-col">
                  <GemLightIcon className="size-10 shrink-0 fill-purple-700" />
                  <Chip
                    className="h-fit bg-[#9048C7] uppercase text-white"
                    label={t('core.preferred')}
                    variant="filled"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-medium">{t('builder.sixtyDayPreferredOffer')}</span>
                  </div>
                  <div>{t('builder.saveTimeWithAcceleration')}</div>
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-2xl font-bold">
                  {asNumber({ value: preferredRateData.preferredRate, decimals: 2 })}% {t('core.apr')}
                </div>
                <div className="text-text-secondary line-through">
                  {asNumber({ value: preferredRateData.selectedRate, decimals: 2 })}% {t('core.apr')}
                </div>
              </div>
            </div>
            {/* Discount Row */}
            <div className="mb-4 flex items-center justify-between rounded-lg bg-purple-50 p-4">
              <span>{t('builder.yourNewDiscount')}</span>
              <span className="font-medium">{asCurrency(-Math.abs(preferredRateData.preferredDiscount), 'USD')}</span>
            </div>
            {/* Bottom Row */}
            <div className="flex flex-col gap-4 rounded-lg bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
              <ul className="list-disc pl-4">
                <li>{t('builder.guaranteedFunding')}</li>
                <li>{t('builder.appliesToAllInvoices')}</li>
                <li>{t('builder.noEditingYourOffer')}</li>
              </ul>
              <div className="flex flex-col gap-2 sm:items-end">
                {!preferredRateSelected ? (
                  <Button className="w-fit sm:w-full" onClick={handleUpgrade}>
                    {t('builder.upgradeToPreferred')}
                  </Button>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-lg font-bold text-secondary-700">
                      <span>
                        <CheckIcon className="size-6 fill-secondary-700" />
                      </span>
                      <span>{t('builder.upgradedToPreferred')}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            {preferredRateSelected && (
              <div className="mt-4 sm:text-right">
                <span className="text-text-secondary">{t('builder.changeYourMind')}</span>
                <button className="ml-1 font-medium text-blue-500" onClick={handleUndoUpgrade} type="button">
                  {t('builder.undoUpgrade')}
                </button>
              </div>
            )}
          </div>
          {/* Division Impact Section - only show when not upgraded */}
          {!preferredRateSelected && (
            <div className="mt-6">
              <div className="mt-4 rounded-lg bg-white">
                <div className="border-b border-stroke p-4 text-lg font-medium">{t('builder.divisionImpact')}</div>
                {buyers.map((buyer) => (
                  <div key={buyer.makerOrganizationUuid} className="p-4">
                    <div className="mb-2 text-base font-semibold">{buyer.makerOrganizationName}</div>
                    <div className="space-y-2">
                      {buyer.takerMarkets.map((tm) => {
                        const supplierMarket = supplierMarketMap.get(`${tm.marketUuid}-${tm.takerDivisionUuid}`);
                        const preferredRate = supplierMarket?.preferred?.oneTime?.rate;
                        const selectedRate = workingTakerMarkets[buildTakerMarketKey(tm)]?.rate;

                        if (!preferredRate || !selectedRate) {
                          return null;
                        }

                        return (
                          <div key={buildTakerMarketKey(tm)} className="rounded-xl bg-canvas p-4">
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-10">
                              <div>
                                <div className="text-sm font-medium">{getTakerMarketName(tm)}</div>
                                <div className="text-sm font-medium">{getTakerMarketDivisionTitle(tm)?.content}</div>
                                <div className="flex items-center gap-1 text-sm text-text-secondary">
                                  <div>{t('core.nameYourRate')}</div>
                                  <div className="h-[3px] w-[3px] rounded-full bg-text-secondary" />
                                  <div>{tm.currency}</div>
                                </div>
                              </div>
                              <div className="shrink-0 sm:ml-auto sm:text-right">
                                <div className="flex items-center gap-1">
                                  <div className="mt-[1px] text-sm text-text-secondary line-through">
                                    {asNumber({ value: selectedRate, decimals: 2 })}% {t('core.apr')}
                                  </div>
                                  <div className="font-bold">
                                    {asNumber({ value: preferredRate, decimals: 2 })}% {t('core.apr')}
                                  </div>
                                </div>
                                <div className="text-sm text-text-secondary">
                                  {asCurrency(tm.eligibleInvoiceAmount, tm.currency)}
                                </div>
                                <div className="text-sm text-text-secondary">
                                  {asNumber({ value: tm.eligibleDpeWeightedAvg, decimals: 0 })}{' '}
                                  {t('core.daysPaidEarly')}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Right Column - Offer Summary */}
        <div className="flex-1">
          <div className="lg:sticky lg:top-24">
            <div className="mb-2 text-lg font-medium">{t('builder.offerSummary')}</div>
            <div className="rounded-lg bg-deepBlue-50 p-2">
              <OfferSummary offerSubmitting={false} step="preferred-upgrade" />
            </div>
          </div>
        </div>
      </div>
      {/* Navigation */}
      {!disableNavigation && (
        <PageBottomPortal>
          <NavigationBar>
            <div className="flex w-full shrink-0 flex-col items-center gap-4 md:ml-auto md:w-fit md:flex-row">
              <Button className="w-full md:w-fit" variant="secondary" onClick={onBackClick}>
                {t('core.back')}
              </Button>
              <Button className="w-full md:w-fit" onClick={onNextClick}>
                {t('core.next')}
              </Button>
            </div>
          </NavigationBar>
        </PageBottomPortal>
      )}
    </>
  );
};

export const PreferredRateStep = () => {
  return (
    <QueryBoundaries LoadingComponent={() => <PreferredRateStepSkeleton />}>
      <PreferredRateStepContent />
    </QueryBoundaries>
  );
};
